import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getHeaders } from '../../utility/getHeader';
import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import {
  Card,
  Row,
  Col,
  Statistic,
  Avatar,
  Table,
  Tag,
  Space,
  Typography,
  Button,
  List,
  Spin,
  Empty,
} from 'antd';
import {
  DollarOutlined,
  FileTextOutlined,
  WalletOutlined,
  SwapOutlined,
  LineChartOutlined,
  BarChartOutlined,
  PieChartOutlined,
  AreaChartOutlined,
  SnippetsOutlined,
  TeamOutlined,
  ShopOutlined,
  CalendarOutlined,
  EyeOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer
} from 'recharts';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const { Title, Text } = Typography;

const TXN_TYPE_COLOR = { Credit: 'green', Debit: 'red' };

function AccountDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  // Summary Cards Data
  const [summaryData, setSummaryData] = useState({
    totalReceivables: 0,
    totalPayables: 0,
    bankBalance: 0,
    cashInHand: 0,
    todayReceipts: 0,
    todayPayments: 0,
    monthlyRevenue: 0,
    monthlyExpenses: 0
  });

  // Recent Transactions
  const [recentTransactions, setRecentTransactions] = useState([]);

  // Ledger Summaries
  const [customerLedgerSummary, setCustomerLedgerSummary] = useState({
    totalCustomers: 0,
    activeCustomers: 0,
    outstandingAmount: 0
  });

  const [supplierLedgerSummary, setSupplierLedgerSummary] = useState({
    totalSuppliers: 0,
    activeSuppliers: 0,
    outstandingAmount: 0
  });

  // Bank Accounts Summary
  const [bankAccountsSummary, setBankAccountsSummary] = useState([]);

  // Vouchers Summary
  const [vouchersSummary, setVouchersSummary] = useState({
    totalReceiptVouchers: 0,
    totalPaymentVouchers: 0,
    todayReceiptVouchers: 0,
    todayPaymentVouchers: 0
  });

  // Chart Data
  const [monthlyTrendsData, setMonthlyTrendsData] = useState([]);
  const [topCustomersData, setTopCustomersData] = useState([]);
  const [topSuppliersData, setTopSuppliersData] = useState([]);
  const [cashFlowData, setCashFlowData] = useState([]);

  useEffect(() => {
    fetchAllAccountData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllAccountData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchSummaryData(),
        fetchBankAccountsSummary()
      ]);
    } catch (error) {
      console.error('Error fetching account data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummaryData = async () => {
    try {
      const response = await axios.get('/account/dashboard/summary', getHeaders());
      if (response.data.success) {
        const data = response.data.data;

        setSummaryData({
          totalReceivables: data.customer_ledger?.net_receivable || 0,
          totalPayables: data.supplier_ledger?.net_payable || 0,
          bankBalance: data.bank_accounts?.bank_balance || 0,
          cashInHand: data.bank_accounts?.cash_balance || 0,
          todayReceipts: 0,
          todayPayments: 0,
          monthlyRevenue: data.sales?.total_sales || 0,
          monthlyExpenses: data.payments?.total_payment_amount || 0
        });

        setRecentTransactions(data.recent_transactions || []);

        setCustomerLedgerSummary({
          totalCustomers: data.customer_ledger?.total_customers || 0,
          activeCustomers: data.customer_ledger?.total_customers || 0,
          outstandingAmount: data.customer_ledger?.net_receivable || 0
        });

        setSupplierLedgerSummary({
          totalSuppliers: data.supplier_ledger?.total_suppliers || 0,
          activeSuppliers: data.supplier_ledger?.total_suppliers || 0,
          outstandingAmount: data.supplier_ledger?.net_payable || 0
        });

        setVouchersSummary({
          totalReceiptVouchers: data.receipts?.total_receipts || 0,
          totalPaymentVouchers: data.payments?.total_payments || 0,
          todayReceiptVouchers: 0,
          todayPaymentVouchers: 0
        });

        if (data.monthly_trends) {
          const trendsData = data.monthly_trends.map(item => ({
            month: item.month,
            credits: parseFloat(item.total_credits || 0),
            debits: parseFloat(item.total_debits || 0),
            transactions: parseInt(item.transaction_count || 0)
          }));
          setMonthlyTrendsData(trendsData.reverse());
        }

        if (data.top_customers) {
          const customersData = data.top_customers.map(item => ({
            name: item.customer_name || `Customer ${item.account_id}`,
            outstanding: parseFloat(item.outstanding || 0)
          }));
          setTopCustomersData(customersData);
        }

        if (data.top_suppliers) {
          const suppliersData = data.top_suppliers.map(item => ({
            name: item.supplier_name || `Supplier ${item.supplier_id}`,
            outstanding: parseFloat(item.outstanding || 0)
          }));
          setTopSuppliersData(suppliersData);
        }

        const cashFlow = [
          { name: 'Cash Receipts', value: parseFloat(data.receipts?.cash_receipts || 0), color: '#0d9488' },
          { name: 'Bank Receipts', value: parseFloat(data.receipts?.bank_receipts || 0), color: '#2563eb' },
          { name: 'Cash Payments', value: parseFloat(data.payments?.cash_payments || 0), color: '#e11d48' },
          { name: 'Bank Payments', value: parseFloat(data.payments?.bank_payments || 0), color: '#d97706' }
        ];
        setCashFlowData(cashFlow);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
      toast.error('Failed to load dashboard summary');
    }
  };

  const fetchBankAccountsSummary = async () => {
    try {
      const response = await axios.get('/bank-accounts', getHeaders());
      if (response.data.success) {
        setBankAccountsSummary(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
    }
  };

  const formatCurrency = (amount) => {
    return `฿${Number(amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Gradient hero stat card for the top financial-overview row
  const GradientStatCard = ({ title, value, icon, gradient, suffixText, onClick }) => (
    <Card
      hoverable={!!onClick}
      onClick={onClick}
      style={{ background: gradient, border: 'none', borderRadius: 12, cursor: onClick ? 'pointer' : 'default' }}
      styles={{ body: { padding: 14 } }}
    >
      <Avatar shape="square" size={28} icon={icon} style={{ background: 'rgba(255,255,255,0.25)', marginBottom: 8, fontSize: 14 }} />
      <div>
        <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</Text>
        <Title level={4} style={{ color: '#fff', margin: '2px 0 4px' }}>{formatCurrency(value)}</Title>
        {suffixText && (
          <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11 }}>
            <EyeOutlined style={{ marginRight: 4 }} />{suffixText}
          </Text>
        )}
      </div>
    </Card>
  );

  // Plain white stat card with a colored accent, used for the secondary KPI row
  const StatCard = ({ title, value, icon, color, onClick }) => (
    <Card
      hoverable={!!onClick}
      onClick={onClick}
      style={{ borderRadius: 14, borderLeft: `4px solid ${color}`, cursor: onClick ? 'pointer' : 'default' }}
    >
      <Row justify="space-between" align="middle">
        <Col>
          <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</Text>
          <Title level={4} style={{ margin: '4px 0 0' }}>{formatCurrency(value)}</Title>
        </Col>
        <Col>
          <Avatar shape="square" size={48} icon={icon} style={{ backgroundColor: `${color}1A`, color }} />
        </Col>
      </Row>
    </Card>
  );

  // Square icon tile used in the Quick Access grid
  const QuickAccessCard = ({ title, icon, color, path, count }) => (
    <Card
      hoverable
      onClick={() => navigate(path)}
      style={{ borderRadius: 14, textAlign: 'center', cursor: 'pointer' }}
      styles={{ body: { padding: '24px 12px' } }}
    >
      <Avatar size={56} icon={icon} style={{ backgroundColor: `${color}1A`, color, marginBottom: 12 }} />
      <div><Text strong>{title}</Text></div>
      {count !== undefined && <Tag color={color} style={{ marginTop: 8 }}>{count} records</Tag>}
    </Card>
  );

  const chartTooltipStyle = {
    contentStyle: {
      backgroundColor: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: 8,
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
    },
    formatter: (value) => `฿${Number(value).toLocaleString('th-TH', { minimumFractionDigits: 2 })}`
  };

  const transactionColumns = [
    { title: 'Date', dataIndex: 'transaction_date', key: 'date', render: (v) => formatDate(v) },
    { title: 'Trans ID', dataIndex: 'transaction_id', key: 'transaction_id', render: (v) => <Tag>{v}</Tag> },
    { title: 'Type', dataIndex: 'transaction_type', key: 'type', render: (v) => <Tag color={TXN_TYPE_COLOR[v] || 'blue'}>{v}</Tag> },
    { title: 'Category', dataIndex: 'transaction_category', key: 'category', render: (v) => <Tag color="cyan">{v}</Tag> },
    { title: 'Bank', dataIndex: 'bank_name', key: 'bank_name' },
    { title: 'Description', dataIndex: 'description', key: 'description', render: (v) => v || '-', ellipsis: true },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', align: 'right', render: (v) => <Text strong>{formatCurrency(v)}</Text> },
    { title: 'Balance', dataIndex: 'balance_after', key: 'balance_after', align: 'right', render: (v) => <Text type="secondary">{formatCurrency(v)}</Text> },
  ];

  if (loading) {
    return (
      <Layout>
        <Header title="Account Dashboard" />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <Spin size="large" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header title="Account Dashboard" />
      <ToastContainer />

      <div style={{ padding: 20 }}>
        {/* Welcome Banner */}
        <Card
          style={{ background: 'linear-gradient(135deg, #0d9488 0%, #0f172a 100%)', border: 'none', borderRadius: 16, marginBottom: 24 }}
          styles={{ body: { padding: '28px 32px' } }}
        >
          <Row justify="space-between" align="middle" gutter={[16, 16]}>
            <Col>
              <Title level={3} style={{ color: '#fff', margin: 0 }}>📊 Account Dashboard</Title>
              <Text style={{ color: 'rgba(255,255,255,0.8)' }}>Complete overview of all accounting activities and financial summaries</Text>
            </Col>
            <Col>
              <Button icon={<ReloadOutlined />} onClick={fetchAllAccountData} loading={loading}>
                Refresh Data
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Financial Overview */}
        <Title level={5} style={{ marginBottom: 16 }}>
          <DollarOutlined style={{ marginRight: 8, color: '#0d9488' }} />
          Financial Overview
        </Title>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} md={12} lg={6}>
            <GradientStatCard
              title="Total Receivables"
              value={summaryData.totalReceivables}
              icon={<DollarOutlined />}
              gradient="linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)"
              suffixText="View Customer Ledger"
              onClick={() => navigate('/reports/saleledger')}
            />
          </Col>
          <Col xs={24} md={12} lg={6}>
            <GradientStatCard
              title="Total Payables"
              value={summaryData.totalPayables}
              icon={<FileTextOutlined />}
              gradient="linear-gradient(135deg, #e11d48 0%, #fb7185 100%)"
              suffixText="View Supplier Ledger"
              onClick={() => navigate('/reports/supplierledger')}
            />
          </Col>
          <Col xs={24} md={12} lg={6}>
            <GradientStatCard
              title="Bank Balance"
              value={summaryData.bankBalance}
              icon={<WalletOutlined />}
              gradient="linear-gradient(135deg, #2563eb 0%, #60a5fa 100%)"
              suffixText="Manage Bank Accounts"
              onClick={() => navigate('/banking/accounts')}
            />
          </Col>
          <Col xs={24} md={12} lg={6}>
            <GradientStatCard
              title="Cash in Hand"
              value={summaryData.cashInHand}
              icon={<DollarOutlined />}
              gradient="linear-gradient(135deg, #d97706 0%, #fbbf24 100%)"
              suffixText="Available Cash"
            />
          </Col>
        </Row>

        {/* Secondary KPIs */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} md={12} lg={6}>
            <StatCard
              title="Today's Receipts"
              value={summaryData.todayReceipts}
              icon={<SnippetsOutlined />}
              color="#0891b2"
              onClick={() => navigate('/vouchers/recieptvoucher')}
            />
          </Col>
          <Col xs={24} md={12} lg={6}>
            <StatCard
              title="Today's Payments"
              value={summaryData.todayPayments}
              icon={<SwapOutlined />}
              color="#6366f1"
              onClick={() => navigate('/vouchers/paymentvoucher')}
            />
          </Col>
          <Col xs={24} md={12} lg={6}>
            <StatCard
              title="Monthly Revenue"
              value={summaryData.monthlyRevenue}
              icon={<LineChartOutlined />}
              color="#059669"
            />
          </Col>
          <Col xs={24} md={12} lg={6}>
            <StatCard
              title="Monthly Expenses"
              value={summaryData.monthlyExpenses}
              icon={<BarChartOutlined />}
              color="#db2777"
            />
          </Col>
        </Row>

        {/* Quick Access */}
        <Title level={5} style={{ marginBottom: 16 }}>Quick Access</Title>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={8} md={4}>
            <QuickAccessCard title="Customer Ledger" icon={<TeamOutlined />} color="#2563eb" path="/reports/saleledger" count={customerLedgerSummary.totalCustomers} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <QuickAccessCard title="Supplier Ledger" icon={<ShopOutlined />} color="#16a34a" path="/reports/supplierledger" count={supplierLedgerSummary.totalSuppliers} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <QuickAccessCard title="Receipt Vouchers" icon={<SnippetsOutlined />} color="#0891b2" path="/vouchers/recieptvoucher" count={vouchersSummary.totalReceiptVouchers} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <QuickAccessCard title="Payment Vouchers" icon={<FileTextOutlined />} color="#6366f1" path="/vouchers/paymentvoucher" count={vouchersSummary.totalPaymentVouchers} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <QuickAccessCard title="Bank Accounts" icon={<WalletOutlined />} color="#d97706" path="/banking/accounts" count={bankAccountsSummary.length} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <QuickAccessCard title="General Ledger" icon={<LineChartOutlined />} color="#dc2626" path="/expenses/suppliersexpenses" />
          </Col>
        </Row>

        {/* Bank Accounts & Recent Transactions */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={10}>
            <Card
              style={{ borderRadius: 14, height: '100%' }}
              title={<Space><WalletOutlined style={{ color: '#0d9488' }} /><Text strong>Bank Accounts</Text></Space>}
              extra={<Button size="small" icon={<EyeOutlined />} onClick={() => navigate('/banking/accounts')}>Manage</Button>}
            >
              {bankAccountsSummary.length === 0 ? (
                <Empty description="No bank accounts found" />
              ) : (
                <List
                  dataSource={bankAccountsSummary.slice(0, 5)}
                  renderItem={(account) => {
                    const positive = parseFloat(account.current_balance) >= 0;
                    return (
                      <List.Item>
                        <List.Item.Meta
                          avatar={<Avatar icon={<WalletOutlined />} style={{ backgroundColor: '#0d948822', color: '#0d9488' }} />}
                          title={account.account_name}
                          description={`${account.bank_name} - ${account.account_number}`}
                        />
                        <Text strong style={{ color: positive ? '#16a34a' : '#dc2626' }}>{formatCurrency(account.current_balance)}</Text>
                      </List.Item>
                    );
                  }}
                />
              )}
              {bankAccountsSummary.length > 5 && (
                <div style={{ textAlign: 'center', marginTop: 12 }}>
                  <Button size="small" onClick={() => navigate('/banking/accounts')}>
                    View All ({bankAccountsSummary.length}) Accounts
                  </Button>
                </div>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={14}>
            <Card
              style={{ borderRadius: 14, height: '100%' }}
              title={<Space><CalendarOutlined style={{ color: '#0d9488' }} /><Text strong>Recent Transactions</Text></Space>}
            >
              <Table
                rowKey={(row, index) => row.id || index}
                columns={transactionColumns}
                dataSource={recentTransactions}
                pagination={{ pageSize: 5 }}
                scroll={{ x: true }}
                size="small"
                locale={{ emptyText: 'No recent transactions' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Charts and Graphs */}
        <Title level={5} style={{ marginBottom: 16 }}>
          <LineChartOutlined style={{ marginRight: 8, color: '#0d9488' }} />
          Financial Analytics &amp; Trends
        </Title>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {/* Monthly Trends */}
          <Col xs={24} lg={16}>
            <Card
              style={{ borderRadius: 14 }}
              title={<Space><AreaChartOutlined style={{ color: '#0d9488' }} /><Text strong>Monthly Transaction Trends</Text></Space>}
              styles={{ header: { border: 'none' } }}
            >
              <Text type="secondary" style={{ fontSize: 12 }}>Last 6 months credit vs debit comparison</Text>
              {monthlyTrendsData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={monthlyTrendsData}>
                    <defs>
                      <linearGradient id="colorCredits" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0d9488" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#0d9488" stopOpacity={0.1} />
                      </linearGradient>
                      <linearGradient id="colorDebits" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#e11d48" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#e11d48" stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" stroke="#666" style={{ fontSize: 12 }} />
                    <YAxis stroke="#666" style={{ fontSize: 12 }} />
                    <Tooltip {...chartTooltipStyle} />
                    <Legend wrapperStyle={{ paddingTop: 20 }} iconType="circle" />
                    <Area type="monotone" dataKey="credits" stroke="#0d9488" fillOpacity={1} fill="url(#colorCredits)" name="Credits" strokeWidth={2} />
                    <Area type="monotone" dataKey="debits" stroke="#e11d48" fillOpacity={1} fill="url(#colorDebits)" name="Debits" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Empty description="No transaction data available" style={{ padding: '40px 0' }} />
              )}
            </Card>
          </Col>

          {/* Cash Flow Pie */}
          <Col xs={24} lg={8}>
            <Card
              style={{ borderRadius: 14 }}
              title={<Space><PieChartOutlined style={{ color: '#0d9488' }} /><Text strong>Cash Flow Distribution</Text></Space>}
            >
              <Text type="secondary" style={{ fontSize: 12 }}>Receipts vs Payments breakdown</Text>
              {cashFlowData.filter(item => item.value > 0).length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={cashFlowData.filter(item => item.value > 0)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      dataKey="value"
                    >
                      {cashFlowData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip {...chartTooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Empty description="No cash flow data available" style={{ padding: '40px 0' }} />
              )}
            </Card>
          </Col>

          {/* Top Customers */}
          <Col xs={24} lg={12}>
            <Card
              style={{ borderRadius: 14 }}
              title={<Space><TeamOutlined style={{ color: '#2563eb' }} /><Text strong>Top 5 Customers by Outstanding</Text></Space>}
            >
              <Text type="secondary" style={{ fontSize: 12 }}>Highest receivables</Text>
              {topCustomersData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={topCustomersData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" stroke="#666" style={{ fontSize: 12 }} />
                    <YAxis dataKey="name" type="category" width={120} stroke="#666" style={{ fontSize: 11 }} />
                    <Tooltip {...chartTooltipStyle} />
                    <Bar dataKey="outstanding" fill="#2563eb" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Empty description="No customer data available" style={{ padding: '40px 0' }} />
              )}
            </Card>
          </Col>

          {/* Top Suppliers */}
          <Col xs={24} lg={12}>
            <Card
              style={{ borderRadius: 14 }}
              title={<Space><ShopOutlined style={{ color: '#d97706' }} /><Text strong>Top 5 Suppliers by Outstanding</Text></Space>}
            >
              <Text type="secondary" style={{ fontSize: 12 }}>Highest payables</Text>
              {topSuppliersData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={topSuppliersData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" stroke="#666" style={{ fontSize: 12 }} />
                    <YAxis dataKey="name" type="category" width={120} stroke="#666" style={{ fontSize: 11 }} />
                    <Tooltip {...chartTooltipStyle} />
                    <Bar dataKey="outstanding" fill="#d97706" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Empty description="No supplier data available" style={{ padding: '40px 0' }} />
              )}
            </Card>
          </Col>
        </Row>

        {/* Vouchers Summary */}
        <Title level={5} style={{ marginBottom: 16 }}>Vouchers Summary</Title>
        <Row gutter={[16, 16]}>
          <Col xs={12} md={6}>
            <Card style={{ borderRadius: 14, textAlign: 'center' }}>
              <Statistic
                title="Total Receipt Vouchers"
                value={vouchersSummary.totalReceiptVouchers}
                prefix={<SnippetsOutlined />}
                valueStyle={{ color: '#0d9488' }}
              />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card style={{ borderRadius: 14, textAlign: 'center' }}>
              <Statistic
                title="Total Payment Vouchers"
                value={vouchersSummary.totalPaymentVouchers}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: '#e11d48' }}
              />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card style={{ borderRadius: 14, textAlign: 'center' }}>
              <Statistic
                title="Today's Receipts"
                value={vouchersSummary.todayReceiptVouchers}
                prefix={<SnippetsOutlined />}
                valueStyle={{ color: '#0891b2' }}
              />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card style={{ borderRadius: 14, textAlign: 'center' }}>
              <Statistic
                title="Today's Payments"
                value={vouchersSummary.todayPaymentVouchers}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: '#d97706' }}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </Layout>
  );
}

export default AccountDashboard;
